import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../pages/auth/services/auth.service';
import {
  AuditLogPayload,
  AuditLogResponse,
  AuditLogSubmissionResponse,
} from '../../models/audit-log.model';

@Injectable({
  providedIn: 'root',
})
export class AuditLogService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private apiUrl = `${environment.apiBaseUrl}/api/admin/audit-logs`;
  private auditLogApiUrl = `${environment.auditLogApiUrl}/api/audit-log`;

  logFeature(payload: AuditLogPayload): void {
    if (!this.auditLogApiUrl) {
      console.warn('Audit log API URL not configured');
      return;
    }

    const token = this.authService.getStoredToken();
    if (!token) {
      console.warn('No auth token available for audit logging');
      return;
    }

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    });

    const body = {
      featureName: payload.featureName,
      action: payload.action,
      source: 'Client-side',
      meta: payload.meta || {},
      description: payload.description,
    };

    this.http
      .post<AuditLogSubmissionResponse>(this.auditLogApiUrl, body, { headers })
      .pipe(
        catchError((error) => {
          console.error('Audit log failed:', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  getAuditLogs(
    page = 1,
    pageSize = 200,
    filters: {
      connection_name?: string;
      user_id?: number;
      source?: string;
      start_date?: string;
      end_date?: string;
    } = {},
  ): Observable<AuditLogResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    if (filters.connection_name) {
      params = params.set('connection_name', filters.connection_name);
    }
    if (filters.user_id) {
      params = params.set('user_id', filters.user_id.toString());
    }
    if (filters.source) {
      params = params.set('source', filters.source);
    }
    if (filters.start_date) {
      params = params.set('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params = params.set('end_date', filters.end_date);
    }

    return this.http.get<AuditLogResponse>(this.apiUrl, { params });
  }

  getMetaDetails(logId: number): Observable<JSON | null> {
    const url = `${this.apiUrl}/${logId}/meta`;
    return this.http.get<JSON | null>(url);
  }
}
